import { useState, type FormEvent } from 'react'
import { api } from '../../../api/client'

const MAX_IMAGE_BYTES = 1024 * 1024

function base64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		if (file.size > MAX_IMAGE_BYTES) return reject(new Error('Each screenshot must be at most 1 MiB.'))
		const reader = new FileReader()
		reader.onerror = () => reject(new Error('Could not read the screenshot.'))
		reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
		reader.readAsDataURL(file)
	})
}

/**
 * A staff reply with up to three JPEG screenshots. The message id stays the same until the text or screenshots change,
 * so sending again after a failure is a retry, never a second message.
 */
export function ReplyForm({ conversationId, onSent }: { conversationId: string; onSent(): void }) {
	const [text, setText] = useState('')
	const [images, setImages] = useState<File[]>([])
	const [messageId, setMessageId] = useState(() => crypto.randomUUID())
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)

	async function submit(event: FormEvent) {
		event.preventDefault()
		setBusy(true)
		setError(null)
		try {
			const attachments = await Promise.all(
				images.map(async (file) => ({ contentType: 'image/jpeg', data: await base64(file) })),
			)
			await api.post(`/api/v1/admin/support/${conversationId}/reply`, { id: messageId, text, attachments })
			setText('')
			setImages([])
			setMessageId(crypto.randomUUID())
			onSent()
		} catch (e) {
			setError((e as Error).message)
		} finally {
			setBusy(false)
		}
	}

	return (
		<form className="card stack" onSubmit={submit}>
			<label htmlFor="reply-text">Reply</label>
			<textarea
				id="reply-text"
				required
				maxLength={10000}
				rows={5}
				value={text}
				disabled={busy}
				onChange={(e) => {
					setText(e.target.value)
					setMessageId(crypto.randomUUID())
				}}
			/>
			<label htmlFor="reply-images" className="small">
				Screenshots (up to 3 JPEGs, 1 MiB and 2000 pixels each)
			</label>
			<input
				id="reply-images"
				type="file"
				accept="image/jpeg"
				multiple
				disabled={busy}
				onChange={(e) => {
					setImages([...(e.target.files ?? [])].slice(0, 3))
					setMessageId(crypto.randomUUID())
				}}
			/>
			{error && (
				<p className="error small" role="alert">
					{error}
				</p>
			)}
			<div>
				<button type="submit" className="button" disabled={busy || !text.trim()}>
					Send reply
				</button>
			</div>
		</form>
	)
}
